import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../../firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import { useT } from '../i18n';
import { Icon, Modal, Spinner, STORE_LINKS } from '../components/ui';
import { GlowBackground } from '../layout/AppShell';
import { createUserProfile } from '../lib/services';
import { setSignupInProgress } from '../context/Session';
import { SHOW_BLOGS } from '../config';

function authErrorKey(err) {
  switch (err?.code) {
    case 'auth/invalid-email': return 'invalidEmailAddress';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials': return 'incorrectPassword';
    case 'auth/user-not-found': return 'noAccountFoundWithEmail';
    case 'auth/email-already-in-use': return 'emailAlreadyRegistered';
    case 'auth/weak-password': return 'passwordTooWeak';
    case 'auth/too-many-requests': return 'tooManyFailedAttempts';
    case 'auth/user-disabled': return 'accountDisabled';
    case 'auth/operation-not-allowed': return 'operationNotAllowed';
    case 'auth/network-request-failed': return 'networkErrorCheckConnection';
    default: return 'anErrorOccurred';
  }
}

const FEATURES = [
  ['smart_toy', 'var(--gm-workout)', 'landingFeatureProgramTitle', 'landingFeatureProgramDesc'],
  ['restaurant', 'var(--gm-nutrition)', 'landingFeatureNutritionTitle', 'landingFeatureNutritionDesc'],
  ['local_fire_department', 'var(--gm-flame-b)', 'landingFeatureStreakTitle', 'landingFeatureStreakDesc'],
  ['show_chart', 'var(--gm-body)', 'landingFeatureProgressTitle', 'landingFeatureProgressDesc'],
  ['water_drop', 'var(--gm-water)', 'landingFeatureWaterTitle', 'landingFeatureWaterDesc'],
  ['groups', 'var(--gm-gold)', 'landingFeatureTrainerTitle', 'landingFeatureTrainerDesc'],
];

function PolicyModal({ policy, onClose }) {
  const t = useT();
  const [text, setText] = useState(null);
  React.useEffect(() => {
    if (!policy) return;
    setText(null);
    const file = { privacy: 'privacy-policy', terms: 'terms-of-service', consent: 'explicit-consent' }[policy];
    fetch(`/policies/${t.lang}/${file}.txt`).then((r) => r.text()).then(setText).catch(() => setText(t('anErrorOccurred')));
  }, [policy, t]);
  if (!policy) return null;
  const title = { privacy: t('privacyPolicy'), terms: t('termsOfService'), consent: t('explicitConsent') }[policy];
  return (
    <Modal open wide onClose={onClose} title={title} footer={<button type="button" className="gm-btn" onClick={onClose}>{t('ok')}</button>}>
      {text == null ? <Spinner /> : <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 13.5 }}>{text}</div>}
    </Modal>
  );
}

function AuthCard() {
  const t = useT();
  const [mode, setMode] = useState('signin'); // signin | signup | reset
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', password: '', role: 'trainee' });
  const [accept, setAccept] = useState({ privacy: false, terms: false, consent: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [policy, setPolicy] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!f.email.trim()) return setError(t('pleaseEnterYourEmail'));
    if (mode === 'reset') {
      setBusy(true);
      try {
        await sendPasswordResetEmail(auth, f.email.trim());
        setInfo(t('resetLinkSent'));
      } catch (err) {
        setError(t(authErrorKey(err)));
      } finally {
        setBusy(false);
      }
      return null;
    }
    if (!f.password) return setError(t('pleaseEnterYourPassword'));
    setBusy(true);
    try {
      if (mode === 'signin') {
        await signInWithEmailAndPassword(auth, f.email.trim(), f.password);
      } else {
        if (!f.firstName.trim()) throw Object.assign(new Error(), { key: 'pleaseEnterYourFirstName' });
        if (!f.lastName.trim()) throw Object.assign(new Error(), { key: 'pleaseEnterYourLastName' });
        if (f.password.length < 6) throw Object.assign(new Error(), { key: 'passwordMustBeAtLeast6Characters' });
        if (!accept.privacy || !accept.terms || !accept.consent) throw Object.assign(new Error(), { key: 'allPoliciesMustBeAccepted' });
        setSignupInProgress(true);
        try {
          const cred = await createUserWithEmailAndPassword(auth, f.email.trim(), f.password);
          await createUserProfile({ uid: cred.user.uid, email: f.email.trim(), firstName: f.firstName.trim(), lastName: f.lastName.trim(), role: f.role });
        } finally {
          setSignupInProgress(false);
        }
      }
    } catch (err) {
      setError(err.key ? t(err.key) : t(authErrorKey(err)));
    } finally {
      setBusy(false);
    }
    return null;
  };

  const google = async () => {
    setError('');
    setBusy(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      // The session layer creates the default profile for first-time Google users.
    } catch (err) {
      if (err?.code !== 'auth/popup-closed-by-user' && err?.code !== 'auth/cancelled-popup-request') setError(t(authErrorKey(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gm-card gm-auth-card" style={{ background: 'var(--gm-glass-strong)' }}>
      {mode !== 'reset' ? (
        <div className="gm-segment gm-auth-tabs">
          <button type="button" className={mode === 'signin' ? 'active' : ''} onClick={() => { setMode('signin'); setError(''); }}>{t.upper(t('signIn'))}</button>
          <button type="button" className={mode === 'signup' ? 'active' : ''} onClick={() => { setMode('signup'); setError(''); }}>{t.upper(t('signUp'))}</button>
        </div>
      ) : (
        <div className="gm-row" style={{ marginBottom: 16 }}>
          <button type="button" className="gm-icon-btn" onClick={() => setMode('signin')}><Icon name="arrow_back" /></button>
          <div className="gm-card-title">{t('forgotPassword')}</div>
        </div>
      )}

      <form className="gm-stack" style={{ gap: 12 }} onSubmit={submit}>
        {mode === 'signup' && (
          <>
            <div className="gm-grid-2 keep">
              <div className="gm-field"><label>{t('firstName')}</label><input className="gm-input" value={f.firstName} onChange={set('firstName')} maxLength={30} autoComplete="given-name" /></div>
              <div className="gm-field"><label>{t('lastName')}</label><input className="gm-input" value={f.lastName} onChange={set('lastName')} maxLength={30} autoComplete="family-name" /></div>
            </div>
            <div className="gm-field">
              <label>{t('iAmA')}</label>
              <div className="gm-segment">
                <button type="button" className={f.role === 'trainee' ? 'active' : ''} onClick={() => setF({ ...f, role: 'trainee' })}>{t('onboardingRoleOptionAthlete')}</button>
                <button type="button" className={f.role === 'trainer' ? 'active' : ''} onClick={() => setF({ ...f, role: 'trainer' })}>{t('onboardingRoleOptionTrainer')}</button>
              </div>
            </div>
          </>
        )}
        <div className="gm-field"><label>{t('email')}</label><input className="gm-input" type="email" value={f.email} onChange={set('email')} autoComplete="email" maxLength={100} /></div>
        {mode !== 'reset' && (
          <div className="gm-field">
            <label>{t('password')}</label>
            <input className="gm-input" type="password" value={f.password} onChange={set('password')} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} maxLength={128} />
          </div>
        )}
        {mode === 'signin' && (
          <div style={{ textAlign: 'right', marginTop: -4 }}>
            <button type="button" className="gm-link" onClick={() => { setMode('reset'); setError(''); }}>{t('forgotPassword')}</button>
          </div>
        )}
        {mode === 'signup' && (
          <div className="gm-stack-sm">
            {[['privacy', 'iAcceptPrivacyPolicy'], ['terms', 'iAcceptTermsOfService'], ['consent', 'iAcceptExplicitConsent']].map(([k, label]) => (
              <label key={k} className="gm-check">
                <input type="checkbox" checked={accept[k]} onChange={(e) => setAccept({ ...accept, [k]: e.target.checked })} />
                <span>
                  {t(label)}{' '}
                  <button type="button" className="gm-link" onClick={(e) => { e.preventDefault(); setPolicy(k); }}>({t('viewPolicy')})</button>
                </span>
              </label>
            ))}
          </div>
        )}
        {error && <div className="gm-banner gm-banner--warn"><Icon name="error" size={18} />{error}</div>}
        {info && <div className="gm-banner"><Icon name="mark_email_read" size={18} />{info}</div>}
        <button type="submit" className="gm-btn gm-btn--lg gm-btn--block" disabled={busy}>
          {busy && <Spinner />}
          {mode === 'signin' ? t('signIn') : mode === 'signup' ? t('createAccount') : t('sendResetLink')}
        </button>
      </form>

      {mode !== 'reset' && (
        <>
          <div className="gm-divider">{t('orContinueWith')}</div>
          <button type="button" className="gm-btn gm-btn--lg gm-btn--block gm-google-btn" onClick={google} disabled={busy}>
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
            {t('signInWithGoogle')}
          </button>
        </>
      )}
      <PolicyModal policy={policy} onClose={() => setPolicy(null)} />
    </div>
  );
}

export default function Landing() {
  const t = useT();
  const { language, setLanguage } = useLanguage();
  return (
    <div className="gm">
      <GlowBackground photo="/assets/backgrounds/guest-discovery-background.jpg" />
      <div className="gm-landing">
        <nav className="gm-landing__nav">
          <Link to="/" className="gm-brand" style={{ padding: 0 }}>
            <img src="/gymgm_logo.jpg" alt="" />
            <span>GYM<b>GM</b></span>
          </Link>
          <span className="gm-grow" />
          {SHOW_BLOGS && <Link to="/blogs" className="gm-btn gm-btn--ghost gm-btn--sm">{t('blog')}</Link>}
          <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={() => setLanguage(language === 'tr' ? 'en' : 'tr')}>
            <Icon name="language" size={18} /> {language === 'tr' ? 'EN' : 'TR'}
          </button>
        </nav>
        <div className="gm-landing__hero">
          <div className="gm-enter">
            <h1 className="gm-hero-title">
              {t.lang === 'tr' ? <>Antrenmanın, beslenmen ve gelişimin — <em>tek bir yerde.</em></> : <>Your training, nutrition and progress — <em>in one place.</em></>}
            </h1>
            <p className="gm-hero-sub">{t('landingSubtitle')}</p>
            <div className="gm-feature-grid">
              {FEATURES.map(([icon, color, title, desc]) => (
                <div key={title} className="gm-feature">
                  <div className="gm-feature__icon" style={{ background: `color-mix(in srgb, ${color} 16%, transparent)`, color }}><Icon name={icon} /></div>
                  <div>
                    <div className="gm-feature__title">{t(title)}</div>
                    <div className="gm-feature__desc">{t(desc)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="gm-store-badges">
              <a className="gm-btn gm-btn--outline" href={STORE_LINKS.play} target="_blank" rel="noreferrer"><Icon name="shop" /> {t('openPlayStore')}</a>
              {STORE_LINKS.ios && <a className="gm-btn gm-btn--outline" href={STORE_LINKS.ios} target="_blank" rel="noreferrer"><Icon name="phone_iphone" /> {t('openAppStore')}</a>}
            </div>
          </div>
          <AuthCard />
        </div>
        <footer className="gm-landing__footer">
          <span>© {new Date().getFullYear()} GymGM. {t('footerRights')}</span>
          <Link to={`/privacy-policy/${t.lang}`}>{t('privacyPolicy')}</Link>
          <Link to={`/terms-of-service/${t.lang}`}>{t('termsOfService')}</Link>
          {SHOW_BLOGS && <Link to="/blogs">{t('blog')}</Link>}
        </footer>
      </div>
    </div>
  );
}
